import { Check, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { useExamContext } from "@/contexts/ExamContext";
import { cn } from "@/lib/utils";

export function ExamSwitcher() {
  const [open, setOpen] = useState(false);
  const { selectedExamSlug, setSelectedExamSlug, availableExams, isLoading } = useExamContext();

  // Don't show if loading or no exams
  if (isLoading || availableExams.length === 0) {
    return null;
  }

  // Don't show if only one exam (auto-selected)
  if (availableExams.length === 1) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-muted/50 text-sm">
        <GraduationCap className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium">{availableExams[0].name}</span>
      </div>
    );
  }

  const selectedExam = selectedExamSlug 
    ? availableExams.find(e => e.slug === selectedExamSlug)
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[200px] justify-between"
        >
          <div className="flex items-center gap-2">
            <GraduationCap className="h-4 w-4 text-muted-foreground" />
            <span className="truncate">
              {selectedExam ? selectedExam.name : "All Exams"}
            </span>
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0">
        <Command>
          <CommandInput placeholder="Search exams..." />
          <CommandList>
            <CommandEmpty>No exam found.</CommandEmpty>
            <CommandGroup>
              {/* All Exams Option */}
              <CommandItem
                value="all-exams"
                onSelect={() => {
                  setSelectedExamSlug(null);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    selectedExamSlug === null ? "opacity-100" : "opacity-0"
                  )}
                />
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4 text-muted-foreground" />
                  <span>All Exams</span>
                </div>
              </CommandItem>

              {/* Individual Exams */}
              {availableExams.map((exam) => (
                <CommandItem
                  key={exam.slug}
                  value={exam.slug}
                  onSelect={(currentValue) => {
                    setSelectedExamSlug(currentValue === selectedExamSlug ? null : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selectedExamSlug === exam.slug ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <div className="flex items-center gap-2">
                    {exam.icon && <span className="text-base">{exam.icon}</span>}
                    <span>{exam.name}</span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
